import React from 'react';
import { CheckCircle2 } from 'lucide-react';

export type MensajeInformativoVariant = 'info' | 'success' | 'warning' | 'error';

export interface MensajeInformativoProps {
  variant?: MensajeInformativoVariant;
  /** 'md' (default) — tamaño estándar. 'sm' — compacto con texto de 11px. */
  size?: 'sm' | 'md';
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
  /**
   * NUEVO. Letra en gris y sin ícono. El fondo y el borde mantienen el color de la variante.
   * Para mensajes que acompañan a otro y no deben competir con él.
   * Default: false (se ve igual que siempre).
   */
  discreto?: boolean;
  /**
   * NUEVO. Une este mensaje con el adyacente para que ambos se vean como una sola pieza
   * (sin espacio entre ellos y sin redondeo donde se tocan).
   * - 'abajo': este mensaje se une con el que está debajo.
   * - 'arriba': este mensaje se une con el que está arriba.
   * Default: undefined (se ve igual que siempre).
   * Los dos mensajes deben ir seguidos, sin margen entre ellos.
   */
  unido?: 'arriba' | 'abajo';
}

/**
 * Componente para mostrar mensajes informativos, de éxito, advertencia o error.
 *
 * @example
 * ```tsx
 * <MensajeInformativo variant="success">
 *   RUC válido. Datos completados desde SUNAT.
 * </MensajeInformativo>
 *
 * <MensajeInformativo variant="warning">
 *   Este campo es opcional pero recomendado.
 * </MensajeInformativo>
 *
 * <MensajeInformativo
 *   variant="success"
 *   action={<button onClick={handleAction}>Crear cliente</button>}
 * >
 *   RUC válido. Datos completados desde SUNAT.
 * </MensajeInformativo>
 *
 * // NUEVO: dos mensajes unidos, el segundo en modo discreto
 * <div>
 *   <MensajeInformativo variant="success" unido="abajo">
 *     RUC validado correctamente.
 *   </MensajeInformativo>
 *   <MensajeInformativo variant="info" discreto unido="arriba" action={<a>¿Qué significa?</a>}>
 *     Texto de la nota.
 *   </MensajeInformativo>
 * </div>
 * ```
 */
export const MensajeInformativo: React.FC<MensajeInformativoProps> = ({
  variant = 'info',
  size = 'md',
  children,
  className = '',
  action,
  discreto = false,
  unido,
}) => {
  const isSm = size === 'sm';
  const iconSize = isSm ? 12 : 15;

  // Estilos según variante (sin cambios)
  const variantStyles = {
    info: {
      container: 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900/40',
      icon: 'text-blue-600 dark:text-blue-400',
      text: 'text-blue-700 dark:text-blue-400',
    },
    success: {
      container: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900/40',
      icon: 'text-green-600 dark:text-green-400',
      text: 'text-green-700 dark:text-green-400',
    },
    warning: {
      container: 'bg-yellow-50 border-yellow-300 dark:bg-yellow-950/30 dark:border-yellow-900/40',
      icon: 'text-yellow-600 dark:text-yellow-500',
      text: 'text-yellow-800 dark:text-yellow-400',
    },
    error: {
      container: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900/40',
      icon: 'text-red-600 dark:text-red-400',
      text: 'text-red-700 dark:text-red-400',
    },
  };

  const styles = variantStyles[variant];

  // NUEVO: clases derivadas de las opciones
  // En modo discreto el contenido se alinea arriba: si el texto ocupa varias líneas,
  // lo que va al inicio (p. ej. una insignia) y la acción quedan alineados con la primera.
  const alignClass = discreto ? 'items-start' : 'items-center';
  const lineHeight = isSm ? 'leading-4' : 'leading-5';
  const textClass = discreto
    ? `font-normal text-secondary ${lineHeight}`
    : `font-medium ${styles.text}`;
  const actionClass = discreto
    ? `ml-auto shrink-0 flex items-center ${isSm ? 'h-4' : 'h-5'}`
    : 'ml-auto';
  const unidoClass =
    unido === 'abajo' ? 'rounded-b-none border-b-0' :
    unido === 'arriba' ? 'rounded-t-none' :
    '';

  // Íconos según variante (sin cambios)
  const renderIcon = () => {
    switch (variant) {
      case 'success':
        return <CheckCircle2 width={iconSize} height={iconSize} className={`shrink-0 ${styles.icon}`} />;
      case 'warning':
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`shrink-0 ${styles.icon}`}
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'error':
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`shrink-0 ${styles.icon}`}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`shrink-0 ${styles.icon}`}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
    }
  };

  return (
    <div
      className={`flex ${alignClass} gap-2 rounded-lg border ${isSm ? 'px-2 py-1' : 'px-3 py-2'} ${styles.container} ${unidoClass} ${className}`}
    >
      {!discreto && renderIcon()}
      <p className={`${isSm ? 'text-[11px]' : 'text-xs'} ${textClass} flex-1 min-w-0`}>
        {children}
      </p>
      {action && (
        <div className={actionClass}>
          {action}
        </div>
      )}
    </div>
  );
};
